import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { API_URL } from '../config/api';
import { TurfPulseLogoSVG } from '../components/TurfPulseLogo';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Moon, Sun, DownloadSimple, Palette, TShirt, Ruler } from '@phosphor-icons/react';

const BRAND = {
  colors: [
    { name: 'TurfPulse Green', hex: '#0D9256', usage: 'Primary brand color, logo, accents' },
    { name: 'Emerald', hex: '#10B981', usage: 'Taglines, highlights, CTA accents' },
    { name: 'Slate', hex: '#0F172A', usage: 'Dark backgrounds, text' },
    { name: 'White', hex: '#FFFFFF', usage: 'Text on dark, clean backgrounds' },
  ],
  fonts: [
    { name: 'Outfit Bold', usage: 'Headlines, product names, brand text' },
    { name: 'DM Sans', usage: 'Body copy, descriptions' },
    { name: 'Liberation Sans Bold', usage: 'Print materials fallback' },
  ],
  jerseyBack: '/turfpulse-jersey-back-6111.png',
};

const PulseLogoSVG = () => (
  <svg width="200" height="200" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="50" cy="50" r="45" stroke="#0D9256" strokeWidth="3" fill="none" />
    <path d="M 5 50 L 25 50 L 30 50 L 35 35 L 40 65 L 45 20 L 50 80 L 55 45 L 60 50 L 95 50"
      stroke="#0D9256" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

export const VendorAssetsPage = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetch(`${API_URL}/api/merch/products`)
      .then(r => r.json())
      .then(d => setProducts(d.products || []))
      .catch(() => {});
  }, []);

  const downloadImage = (url, name) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.target = '_blank';
    a.click();
  };

  return (
    <div className="min-h-screen bg-background" data-testid="vendor-assets-page">
      <header className="border-b border-border/60 bg-card/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft size={20} />
            </Button>
            <TurfPulseLogoSVG animated={false} size={28} />
            <span className="font-semibold text-base tracking-tight">Vendor Asset Kit</span>
          </div>
          <Button variant="ghost" size="icon" onClick={toggleTheme}>
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-10">

        {/* Intro */}
        <div>
          <h1 className="text-2xl font-bold text-foreground mb-2">TurfPulse Vendor Asset Kit</h1>
          <p className="text-sm text-muted-foreground">
            Everything a print vendor needs — logos, color specs, product mockups, and placement guides.
            Right-click any image to save, or use the download buttons.
          </p>
        </div>

        {/* Brand Logo */}
        <section data-testid="vendor-logo-section">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
            <Palette size={16} /> Brand Logo
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="border border-border/50">
              <CardContent className="p-6 flex flex-col items-center gap-3">
                <div className="bg-white rounded-lg p-4">
                  <PulseLogoSVG />
                </div>
                <p className="text-xs text-muted-foreground text-center">Circle EKG — Green on White</p>
                <p className="text-[10px] text-muted-foreground">Single heartbeat pulse. Use for embroidery, patches, print.</p>
              </CardContent>
            </Card>
            <Card className="border border-border/50">
              <CardContent className="p-6 flex flex-col items-center gap-3">
                <div className="bg-slate-900 rounded-lg p-4">
                  <svg width="200" height="200" viewBox="0 0 100 100" fill="none">
                    <circle cx="50" cy="50" r="45" stroke="#10B981" strokeWidth="3" fill="none" />
                    <path d="M 5 50 L 25 50 L 30 50 L 35 35 L 40 65 L 45 20 L 50 80 L 55 45 L 60 50 L 95 50"
                      stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                </div>
                <p className="text-xs text-muted-foreground text-center">Circle EKG — Green on Dark</p>
                <p className="text-[10px] text-muted-foreground">For dark garments and backgrounds.</p>
              </CardContent>
            </Card>
            <Card className="border border-border/50">
              <CardContent className="p-6 flex flex-col items-center gap-3">
                <div className="bg-[#0D9256] rounded-lg p-4">
                  <svg width="200" height="200" viewBox="0 0 100 100" fill="none">
                    <circle cx="50" cy="50" r="45" stroke="white" strokeWidth="3" fill="none" />
                    <path d="M 5 50 L 25 50 L 30 50 L 35 35 L 40 65 L 45 20 L 50 80 L 55 45 L 60 50 L 95 50"
                      stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                </div>
                <p className="text-xs text-muted-foreground text-center">Circle EKG — White on Green</p>
                <p className="text-[10px] text-muted-foreground">For green garments and branded fabric.</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Color Palette */}
        <section data-testid="vendor-colors-section">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
            <Palette size={16} /> Color Palette
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {BRAND.colors.map(c => (
              <Card key={c.hex} className="border border-border/50 overflow-hidden">
                <div className="h-20" style={{ backgroundColor: c.hex, border: c.hex === '#FFFFFF' ? '1px solid #e5e7eb' : 'none' }} />
                <CardContent className="p-3">
                  <p className="text-sm font-semibold">{c.name}</p>
                  <p className="text-xs font-mono text-muted-foreground">{c.hex}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">{c.usage}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Typography */}
        <section data-testid="vendor-type-section">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Typography</h2>
          <Card className="border border-border/50">
            <CardContent className="p-5 space-y-3">
              {BRAND.fonts.map(f => (
                <div key={f.name} className="flex items-center justify-between">
                  <span className="text-sm font-semibold">{f.name}</span>
                  <span className="text-xs text-muted-foreground">{f.usage}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </section>

        {/* Product Mockups */}
        <section data-testid="vendor-products-section">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
            <TShirt size={16} /> Product Mockups
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.filter(p => p.image).map(p => (
              <Card key={p.id} className="border border-border/50 overflow-hidden">
                <div className="aspect-square bg-muted/30">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <CardContent className="p-3">
                  <h3 className="text-sm font-semibold">{p.name}</h3>
                  <p className="text-[10px] text-muted-foreground mt-0.5 italic">"{p.tagline}"</p>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description}</p>
                  {p.colors && p.colors.length > 0 && (
                    <p className="text-[10px] text-muted-foreground mt-1">Colors: {p.colors.join(', ')}</p>
                  )}
                  <Button
                    size="sm" variant="outline" className="mt-2 w-full text-xs h-7 gap-1"
                    onClick={() => downloadImage(p.image, `turfpulse-${p.id}.png`)}
                  >
                    <DownloadSimple size={12} /> Download Mockup
                  </Button>
                </CardContent>
              </Card>
            ))}

            {/* Jersey Back View */}
            <Card className="border border-border/50 overflow-hidden">
              <div className="aspect-square bg-muted/30">
                <img src={BRAND.jerseyBack} alt="Jersey Back #215" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <CardContent className="p-3">
                <h3 className="text-sm font-semibold">GDD Jersey — Back View</h3>
                <p className="text-[10px] text-muted-foreground mt-0.5 italic">"Isaiah 61:11"</p>
                <p className="text-xs text-muted-foreground mt-1">TURFPULSE nameplate, #6111, Isaiah 61:11 at hem</p>
                <Button
                  size="sm" variant="outline" className="mt-2 w-full text-xs h-7 gap-1"
                  onClick={() => downloadImage(BRAND.jerseyBack, 'turfpulse-jersey-back.png')}
                >
                  <DownloadSimple size={12} /> Download Mockup
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Placement Specs */}
        <section data-testid="vendor-specs-section">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
            <Ruler size={16} /> Placement Specs
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card className="border border-border/50">
              <CardContent className="p-4 space-y-2">
                <h3 className="text-sm font-semibold">Polos & Quarter-Zips</h3>
                <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-4">
                  <li>Circle EKG logo — left chest, embroidered</li>
                  <li>Logo size: 2.5" diameter</li>
                  <li>Thread colors: White on dark garments, Green (#0D9256) on light</li>
                  <li>Single heartbeat pulse only — no double peaks</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="border border-border/50">
              <CardContent className="p-4 space-y-2">
                <h3 className="text-sm font-semibold">T-Shirts</h3>
                <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-4">
                  <li>Screen print or DTG</li>
                  <li>Front chest — centered, 10-12" wide</li>
                  <li>Know Your Number: bold block letters + single EKG spike below</li>
                  <li>Trust the Data: single pulse line + text below, minimal</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="border border-border/50">
              <CardContent className="p-4 space-y-2">
                <h3 className="text-sm font-semibold">Hats</h3>
                <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-4">
                  <li>Trucker: round embroidered patch, front center panel</li>
                  <li>Performance cap: tone-on-tone embroidery, center front</li>
                  <li>Patch size: 2.5-3" diameter</li>
                  <li>Circle EKG with TURFPULSE text around border</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="border border-border/50">
              <CardContent className="p-4 space-y-2">
                <h3 className="text-sm font-semibold">Baseball Jersey</h3>
                <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-4">
                  <li>Front: TURFPULSE in Astros-style italic script, arched</li>
                  <li>Back: TURFPULSE nameplate + #6111</li>
                  <li>Bottom hem (back): Isaiah 61:11</li>
                  <li>Right shoulder sleeve: Circle EKG patch</li>
                  <li>Green pinstripes, green collar/cuff trim</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="border border-border/50">
              <CardContent className="p-4 space-y-2">
                <h3 className="text-sm font-semibold">Patches</h3>
                <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-4">
                  <li>3" diameter woven circle patches</li>
                  <li>Iron-on backing</li>
                  <li>3 colorways: Green/White, Black/Green, White/Green</li>
                  <li>Circle EKG with single heartbeat pulse</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="border border-border/50">
              <CardContent className="p-4 space-y-2">
                <h3 className="text-sm font-semibold">Women's Crewneck</h3>
                <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-4">
                  <li>Front chest: "HE'S OUT THERE AGAIN" in thin modern font</li>
                  <li>Left cuff/wrist: small Circle EKG pulse embroidery</li>
                  <li>Relaxed fit french terry</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Print Files */}
        <section data-testid="vendor-prints-section">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Print-Ready Files</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card className="border border-border/50">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold">Truck Magnet Banner</h3>
                  <p className="text-xs text-muted-foreground">24" x 12" @ 300 DPI</p>
                </div>
                <Button size="sm" variant="outline" className="gap-1 text-xs"
                  onClick={() => downloadImage('/turfpulse-truck-banner-print.png', 'turfpulse-truck-banner-24x12.png')}>
                  <DownloadSimple size={14} /> Download
                </Button>
              </CardContent>
            </Card>
            <Card className="border border-border/50">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold">QR Code (Merch)</h3>
                  <p className="text-xs text-muted-foreground">High-res PNG with branded logo</p>
                </div>
                <Button size="sm" variant="outline" className="gap-1 text-xs"
                  onClick={() => downloadImage('/turfpulse-merch-qr.png', 'turfpulse-qr-code.png')}>
                  <DownloadSimple size={14} /> Download
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Important Notes */}
        <section className="pb-8">
          <Card className="border border-amber-500/20 bg-amber-500/5">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-amber-700 dark:text-amber-400 mb-2">Vendor Notes</h3>
              <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-4">
                <li><strong>Single heartbeat only</strong> — the EKG pulse must show ONE spike. Double peaks = medical red flag</li>
                <li>Logo is always the circle with the pulse line through the center — no variations</li>
                <li>Green is <strong>#0D9256</strong> — match Pantone 7732 C for print</li>
                <li>All AI-generated mockups are concept references — vendor should use vector logo files for final production</li>
                <li>Reach out for vector files (AI/EPS) if needed for embroidery digitizing</li>
              </ul>
            </CardContent>
          </Card>
        </section>
      </main>
    </div>
  );
};
